import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Row from '../../../core/layout/Row';
import Column from '../../../core/layout/Column';
import TextInput from '../../../core/form/TextInput';
import FormButtons from '../../../core/form/FormButtons';
import Select from '../../../core/form/Select';
import { startCase } from 'lodash';
import FetchSelect from '../../../core/form/FetchSelect';
import AudioPreview from '../../../core/layout/AudioPreview';
import LocalFileInput from '../../../core/form/LocalFileInput';
import Button from '../../../core/form/Button';
import Dialog from '../../../core/layout/Dialog';
import CharacterVoiceSearchDialogContainer from './CharacterVoiceSearchDialogContainer';

const CharacterForm = (props) => (
  <>
    <form
      onSubmit={(event) => {
        event.preventDefault();
        props.onSubmit();
      }}
    >
      <Row>
        <Column size={2}>
          <FetchSelect
            required
            label="Academic Plan"
            value={get(props.values, 'academicPlan', '')}
            onChange={(id) => {
              props.onChange('academicPlan', id);
            }}
            disabled={props.submitting || props.isUpdateBlocked}
            errorText={get(props.errors, 'academicPlan', '')}
            url="academic-plans"
            resultTransformer={{
              text: 'name',
              value: 'id',
            }}
          />
        </Column>
        <Column size={3}>
          <TextInput
            required
            disabled={props.submitting || props.isUpdateBlocked}
            label="Name"
            value={get(props.values, 'name', '')}
            onChange={(value) => props.onChange('name', value)}
            errorText={get(props.errors, 'name', null)}
          />
        </Column>
        <Column size={2}>
          <Select
            options={[
              'american',
              'australian',
              'british',
              'canadian',
              'indian',
              'japanese',
              'new-zealand',
              'russian',
              'scottish',
              'spanish',
              'south-african',
            ].map((value) => ({
              value,
              label: startCase(value),
            }))}
            required
            disabled={props.submitting || props.isUpdateBlocked}
            label="Accent"
            value={get(props.values, 'accent', '')}
            errorText={get(props.errors, 'accent', '')}
            onChange={(value) => {
              props.onChange('accent', value);
              props.onChange('elevenLabsVoiceId', '');
              props.onChange('elevenLabsVoicePreview', '');
            }}
          />
        </Column>
        <Column size={5}>
          <TextInput
            required
            label="Voice"
            disabled
            value={`${get(props.values, 'voiceData.name', '')} ${
              get(props.values, 'elevenLabsVoiceId', false) ? `(${get(props.values, 'elevenLabsVoiceId', '')})` : ''
            }`}
          />
          <div style={{ position: 'absolute', top: 44, right: 4 }}>
            <Button
              buttonType="button"
              icon="search"
              onClick={() => props.onVoiceSearch(get(props.values, 'accent', ''))}
              rounded={false}
              disabled={props.submitting || props.isUpdateBlocked || !get(props.values, 'accent', '')}
            />
          </div>
          <div style={{ position: 'absolute', top: 5, right: 5 }}>
            {get(props.values, 'elevenLabsVoicePreview', false) && (
              <AudioPreview src={get(props.values, 'elevenLabsVoicePreview', false)} external />
            )}
          </div>
        </Column>
      </Row>
      <Row>
        <Column size={12}>
          <TextInput
            required
            value={get(props.values, 'imagePrompt', '')}
            label="Image Prompt"
            fieldType="textarea"
            disabled={props.submitting || props.isUpdateBlocked}
            onChange={(value) => props.onChange('imagePrompt', value)}
            errorText={get(props.errors, 'imagePrompt', null)}
          />
          {!get(props.errors, 'imagePrompt', null) && (
            <small style={{ position: 'absolute', bottom: 6, left: 4 }}>
              Example: Male; Age: 16; Ethnicity: Mixed; Physical characteristics: Medium height, athletic build, short
              curly dark hair, brown eyes.
            </small>
          )}
          <div style={{ marginTop: 10, position: 'absolute', right: 4, top: -8 }}>
            <Button
              buttonType="button"
              label="Generate image sample"
              onClick={props.onPreviewImage}
              loading={props.isGeneratingImage}
              disabled={!get(props.values, 'imagePrompt', false)}
            />
          </div>
        </Column>
      </Row>
      <Row>
        <Column size={3}>
          <LocalFileInput
            accept="image"
            label="Reference image (Keeps the character visually consistent between generations)"
            disabled={props.submitting || props.isUpdateBlocked}
            value={get(props.values, 'referenceImage', '')}
            onChange={(file) => props.onChange('referenceImage', file)}
            errorText={get(props.errors, 'referenceImage', '')}
          />
          <small style={{ display: 'block', marginTop: 4 }}>
            On upload, the Image Prompt is rewritten with the art style and traits read from this image.
          </small>
        </Column>
      </Row>
      <Dialog
        title="Image sample"
        isOpen={!!props.sampleImage}
        onCancel={props.onPreviewClose}
        width="65%"
        actions={[]}
      >
        <img alt="sample" src={props.sampleImage} style={{ width: '100%', height: 'auto', objectFit: 'contain' }} />
      </Dialog>
      <FormButtons
        confirmLabel={props.values.id ? 'Update Character' : 'Create Character'}
        isDisabled={props.isUpdateBlocked || props.submitting || !props.isDirty()}
        isSubmitting={props.submitting}
        onReset={props.onReset}
      />
    </form>
    <CharacterVoiceSearchDialogContainer
      onSelect={(data) => {
        props.onChange('voiceData', data);
        props.onChange('elevenLabsVoiceId', data.id);
        props.onChange('elevenLabsVoicePreview', data.previewUrl);
      }}
    />
  </>
);

CharacterForm.propTypes = {
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  onVoiceSearch: PropTypes.func,
  values: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onPreviewImage: PropTypes.func.isRequired,
  onPreviewClose: PropTypes.func.isRequired,
  errors: PropTypes.object,
  submitting: PropTypes.bool,
  isUpdateBlocked: PropTypes.bool,
  isGeneratingImage: PropTypes.bool,
  isDirty: PropTypes.func,
  sampleImage: PropTypes.string,
};

CharacterForm.defaultProps = {
  values: {},
  errors: {},
  submitting: false,
  isDirty: () => false,
  onSubmit: () => alert('submitted'),
  onReset: () => false,
};

export default CharacterForm;
